import { combineLatest, NEVER, Observable, of, Subscription } from 'rxjs';
import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { DsInputStates, Icons } from '@levelaccess/design-system';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  FormControl,
} from '@angular/forms';

import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { PackageNames } from '@shared/constants/packaging';

import { filterRulebaseIfNecessary } from '../../../../utility/rulebase.utility';
import { ExternalLinkService } from '../../../../services/external-link.service';
import {
  IDigitalProperty,
  IDigitalPropertyDetails,
  IDigitalPropertyType,
} from '../../../../../../shared/interfaces/digital-property.interface';
import { CustomValidators } from '../../../../services/helpers/form-custom-validators';
import {
  $digitalProperty,
  $digitalPropertyDefaultTool,
  $digitalPropertyType,
  CHAR_LIMITS,
  ISelectAuditTool,
} from '../../../../../../shared/constants/digital-properties';
import { DigitalPropertyService } from '../../../../services/digital-property.service';
import { WorkspaceService } from '../../../../services/workspace.service';
import { TenantService } from '../../../../services/tenant.service';
import { CommonUtility } from '../../../../utility/common.utility';
import { IComponentFormState } from '../../../../interfaces/advanced-scan-form-state.interface';
import { AuditToolCommonNames, mapAuditToolCommonNamesToDisplayName } from '../../../../../../shared/constants/audit-tool';
import { TranslateService } from '../../../../translate/translate.service';
import { SharedCommonUtility } from '../../../../../../shared/utils/common.utility';
import { IWorkspace } from '../../../../../../shared/interfaces/workspace.interface';
import { $workspace } from '../../../../../../shared/constants/workspace';
import { $privacySettings, $tenant } from '../../../../../../shared/constants/tenant';
import { ITenantInfoWithPackageInfo } from '../../../../../../shared/interfaces/tenant.interface';
import { FeatureFlagCollection } from '../../../../../../shared/interfaces/feature-flag.interface';
import { BannerType } from '../../../../components/banner/banner.component';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';
import { AngularUtility } from '../../../../utility/angular.utility';
import {
  MAX_DIGITAL_PROPERTY_TAG_CHAR_LENGTH,
  MAX_DIGITAL_PROPERTY_TAGS,
} from '../../../../constants/digital-properties-tags.constants';
import { DigitalPropertyTypeUtility } from '../../../../../../shared/utils/digital-property-type.utility';
import { AppConfigService } from '../../../../services/app-config.service';
import { UserAclService } from '../../../../services/user-acl.service';
import { AclSecurityAdapter } from '../../../../services/acl.service';
import { RequiredSecurities } from '../../../../../../shared/constants/required-securities';
import { FormService } from '../../../../services/form.service';

@Component({
  selector: 'app-digital-property-form-details',
  templateUrl: './digital-property-form-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DigitalPropertyFormDetailsComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  private subscription: Subscription;
  private urlChangeSubscription: Subscription;
  private noDomainRestrictionSubscription: Subscription;
  private property: IDigitalProperty | IDigitalPropertyDetails | undefined;
  private existingNames: string[] | null;
  private existingKeys: string[] | null;
  private tenantInfo$: Observable<ITenantInfoWithPackageInfo>;
  public hasAccessibilityStatement: boolean;
  public canRenderMobileBundleId: boolean;

  @Input()
  public workspaceId: string;
  @Input()
  public formValidationRequest$: Observable<void>;
  @Input()
  public shortenHeadingLayout: boolean;
  @Input()
  public disableWorkspaceValidation: boolean;
  @Input()
  public enableDomainRestrictionSettings: boolean;
  @Input()
  public canEditName: boolean;
  @Input()
  public canEditProperty: boolean;
  @Input()
  public canEditTool: boolean;
  @Input()
  public modalMode: boolean = false;
  @Input()
  public tenantId?: string;
  @Output()
  public formChanged: EventEmitter<IComponentFormState<IDigitalProperty>>;

  public form: UntypedFormGroup;
  public digitalPropertyTypes: IDigitalPropertyType[];
  public selectedWorkspace: IWorkspace;
  public $digitalProperty: typeof $digitalProperty;
  public isAdminView: boolean;
  public isWebsite: boolean;
  public isMobile: boolean;
  public auditToolSelectOptions: ISelectAuditTool[];
  public scanningAgentEnabled: boolean;
  public confidentialScanningEnabled: boolean;
  public confidentialScanningEnabledTenantLevel: boolean = false;
  public tagsFeatureEnabled: boolean;
  public digitalAssetsPrivacySettingFeatureEnabled: boolean = false;
  public digitalAssetFormUpdateFeatureEnabled: boolean = false;
  public maxDigitalTag: number = MAX_DIGITAL_PROPERTY_TAGS;
  public maxDigitalTagCharLength: number = MAX_DIGITAL_PROPERTY_TAG_CHAR_LENGTH;
  public readonly charLimits: Readonly<Record<string, number>> = CHAR_LIMITS;
  public readonly digitalAssetDetailsHelpLinkUrl: string;
  public nameError$: Observable<string | null> = of(null);
  public propertyKeyError$: Observable<string | null> = of(null);

  public get confidentialScanningTooltip(): string | undefined {
    return this.confidentialScanningEnabledTenantLevel ? 'confidential_scanning_tenant_level_tooltip' : undefined;
  }

  public get canUpdateDigitalProperties$(): Observable<boolean> {
    return this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useAdmin()
            .useFunctionalActions(RequiredSecurities.Digital_Properties_Update.functionalActions)
            .useForNewSecurityOnly()
            .check(),
        ),
      );
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private digitalPropertyService: DigitalPropertyService,
    private changeDetectorRef: ChangeDetectorRef,
    private workspaceService: WorkspaceService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
    private tenantService: TenantService,
    private formService: FormService,
    private elementRef: ElementRef<HTMLElement>,
    private appConfigService: AppConfigService,
    private userAclService: UserAclService,
    private externalLinkService: ExternalLinkService,
    private injector: Injector,
  ) {
    this.digitalAssetDetailsHelpLinkUrl = this.externalLinkService.getDigitalAssetDetailsHelpUrl();
    this.subscription = new Subscription();
    this.urlChangeSubscription = new Subscription();
    this.$digitalProperty = $digitalProperty;
    this.isAdminView = this.appConfigService.isTenantEnabled() === false;
    this.formChanged = new EventEmitter<IComponentFormState<IDigitalProperty>>();
    this.isWebsite = false;
    this.isMobile = false;
    this.canEditName = true;
    this.canEditProperty = true;
    this.canEditTool = true;
    this.existingKeys = null;
    this.existingNames = null;
    this.scanningAgentEnabled = false;
    this.confidentialScanningEnabled = false;
    this.auditToolSelectOptions = Object.entries(mapAuditToolCommonNamesToDisplayName)
      .filter(([toolName, _]: [AuditToolCommonNames, string]): boolean => toolName !== AuditToolCommonNames.aslint)
      .filter(([toolName, _]: [AuditToolCommonNames, string]): boolean => toolName !== AuditToolCommonNames.mobile_engine)
      .filter(([toolName, _]: [AuditToolCommonNames, string]): boolean =>
        filterRulebaseIfNecessary(this.featureFlagService, toolName),
      )
      .map(([toolName, toolDisplayName]: [AuditToolCommonNames, string]): ISelectAuditTool => {
        return {
          displayName: this.translateService.instant(toolDisplayName),
          value: toolName,
        };
      });
    this.hasAccessibilityStatement = false;
    this.canRenderMobileBundleId = false;

    this.tagsFeatureEnabled = this.featureFlagService.variation(FeatureFlagCollection.digitalPropertyTagging, false);

    this.createForm();
    this.setupFormChanges();
  }

  protected get headingText(): string {
    return this.shortenHeadingLayout ? 'digital_property' : 'label_digital_property_details';
  }

  protected get displayRequiredFieldsLabel(): boolean {
    return !this.shortenHeadingLayout && !this.digitalAssetFormUpdateFeatureEnabled;
  }

  protected get displayDigitalAssetDetailsHelpLink(): boolean {
    return this.digitalAssetFormUpdateFeatureEnabled && SharedCommonUtility.isNullish(this.property);
  }

  private createForm(): void {
    const formControls = {
      [$digitalProperty.name]: new UntypedFormControl('', { validators: this.getNameValidators() }),
      [$digitalProperty.propertyKey]: new UntypedFormControl('', { validators: this.getPropertyKeyValidators() }),
      [$digitalProperty.typeId]: new UntypedFormControl('', [CustomValidators.validateIsEmpty]),
      [$digitalProperty.defaultTool]: new UntypedFormControl($digitalPropertyDefaultTool, [
        CustomValidators.required,
        CustomValidators.validateIsEmpty,
      ]),
      [$digitalProperty.workspaceId]: new UntypedFormControl(''),
      [$digitalProperty.privacyObfuscationEnabled]: new UntypedFormControl(this.confidentialScanningEnabled),
    };

    this.form = this.formBuilder.group(formControls);

    const nameControl: AbstractControl = this.form.get($digitalProperty.name);
    this.nameError$ = this.formService
      .buildFormControlErrorMessage$(nameControl, 'digital_name_label')
      .pipe(map((error: string | null) => (nameControl.touched || nameControl.dirty ? error : null)));

    const propertyKeyControl: AbstractControl = this.form.get($digitalProperty.propertyKey);
    this.propertyKeyError$ = this.formService
      .buildFormControlErrorMessage$(propertyKeyControl, 'digital_property_key_label')
      .pipe(map((error: string | null) => (propertyKeyControl.touched || propertyKeyControl.dirty ? error : null)));
  }

  private getPropertyKeyValidators(): ValidatorFn[] {
    return [
      CustomValidators.validateIsEmpty,
      CustomValidators.digitalPropertyKeyValidator,
      CustomValidators.digitalPropertyFieldWorkspaceDuplicateValidator(this.existingKeys, $digitalProperty.propertyKey),
    ];
  }

  private getNameValidators(): ValidatorFn[] {
    return [
      CustomValidators.validateIsEmpty,
      CustomValidators.digitalPropertyFieldWorkspaceDuplicateValidator(this.existingNames, $digitalProperty.name),
    ];
  }

  private getUrlValidators(): ValidatorFn[] {
    return [CustomValidators.required, CustomValidators.validateIsEmpty, CustomValidators.domainValidator];
  }

  private getMobileBundleIdValidators(): ValidatorFn[] {
    return [CustomValidators.validateIsEmpty, CustomValidators.mobileBundleIdValidator];
  }

  private registerMobileBundleIdField(): void {
    if (this.form.contains($digitalProperty.mobileBundleId)) {
      return;
    }

    const existingBundleId: string | undefined = this.property?.[$digitalProperty.mobileBundleId];

    this.form.registerControl(
      $digitalProperty.mobileBundleId,
      this.formBuilder.control(existingBundleId ?? '', {
        validators: this.getMobileBundleIdValidators(),
      }),
    );
  }

  private isDomainRestrictionEnabled(): boolean {
    return SharedCommonUtility.isBooleanStringTruthy(this.enableDomainRestrictionSettings);
  }

  private isDomainRestrictionRegistered(): boolean {
    return this.form.contains($digitalProperty.noDomainRestrictions);
  }

  protected canRenderDomainRestrictionFields(): boolean {
    return this.isDomainRestrictionEnabled() && this.isDomainRestrictionRegistered();
  }

  protected isDomainRestrictionSupportedByDpType(): boolean {
    return this.isWebsite;
  }

  private registerDomainRestrictionsFields(): void {
    const shouldRegisterDomainRestrictionsFields: boolean =
      this.isDomainRestrictionEnabled() && this.isDomainRestrictionSupportedByDpType();

    if (!shouldRegisterDomainRestrictionsFields) {
      if (this.isDomainRestrictionRegistered()) {
        this.form.removeControl($digitalProperty.noDomainRestrictions);
        this.form.removeControl($digitalProperty.authorizedDomains);
      }
      this.noDomainRestrictionSubscription?.unsubscribe();
      this.noDomainRestrictionSubscription = undefined;
      return;
    }

    if (this.isDomainRestrictionRegistered()) {
      return;
    }

    const checkboxControl: FormControl = this.form.registerControl(
      $digitalProperty.noDomainRestrictions,
      this.formBuilder.control(false),
    );
    this.form.registerControl(
      $digitalProperty.authorizedDomains,
      this.formBuilder.control('', {
        validators: [CustomValidators.validateIsEmpty, CustomValidators.domainValidator],
      }),
    );

    this.noDomainRestrictionSubscription = checkboxControl.valueChanges.subscribe(this.toggleAuthorizedDomainState.bind(this));

    if (SharedCommonUtility.notNullish(this.property)) {
      this.form.patchValue({
        [$digitalProperty.noDomainRestrictions]: this.property[$digitalProperty.noDomainRestrictions],
        [$digitalProperty.authorizedDomains]: {
          value: this.property[$digitalProperty.authorizedDomains].join('\n'),
          disabled: this.property[$digitalProperty.noDomainRestrictions],
        },
      });
    }
  }

  private setupFormChanges(): void {
    const typeChangeSubscription: Subscription = this.form
      .get($digitalProperty.typeId)
      .valueChanges.subscribe((typeId: string) => {
        if (typeof this.digitalPropertyTypes === 'undefined') {
          return;
        }

        const selectedPropertyType: IDigitalPropertyType = this.digitalPropertyTypes.find(
          (type: IDigitalPropertyType) => type[$digitalPropertyType._id] === typeId,
        );

        if (typeof selectedPropertyType === 'undefined') {
          this.isWebsite = false;
          this.isMobile = false;
        } else {
          this.isWebsite = DigitalPropertyTypeUtility.isWebsite(selectedPropertyType);
          this.isMobile = DigitalPropertyTypeUtility.isMobile(selectedPropertyType);
        }

        if (this.isMobile && !this.form.contains($digitalProperty.noBundleIdRestriction)) {
          this.form.registerControl(
            $digitalProperty.noBundleIdRestriction,
            this.formBuilder.control(this.property?.[$digitalProperty.noBundleIdRestriction] ?? false),
          );
        }

        if (this.isWebsite) {
          const urlControl: FormControl = this.form.registerControl(
            $digitalProperty.url,
            this.formBuilder.control('', {
              validators: this.getUrlValidators(),
            }),
          );
          this.urlChangeSubscription = urlControl.valueChanges.subscribe({
            next: this.updateAuthorizedDomains.bind(this),
          });

          if (SharedCommonUtility.notNullish(this.property)) {
            this.form.patchValue({
              [$digitalProperty.url]: this.property[$digitalProperty.url],
            });
          }
        } else {
          this.form.removeControl($digitalProperty.url);
          this.form.removeControl($digitalProperty.accessibilityStatement);
          this.urlChangeSubscription?.unsubscribe();
          this.urlChangeSubscription = undefined;
          this.scanningAgentEnabled = false;
        }

        if (this.isMobile) {
          this.registerMobileBundleIdField();
          this.canRenderMobileBundleId = true;
        } else {
          this.canRenderMobileBundleId = false;
          this.form.removeControl($digitalProperty.mobileBundleId);
          this.form.removeControl($digitalProperty.noBundleIdRestriction);
        }

        this.registerDomainRestrictionsFields();

        this.changeDetectorRef.detectChanges();
      });

    this.subscription.add(typeChangeSubscription);
  }

  private getDigitalPropertyTypes(): Subscription {
    const setDigitalPropertyTypes = (digitalPropertyTypes: IDigitalPropertyType[]): void => {
      this.digitalPropertyTypes = digitalPropertyTypes;
      this.setPropertyTypeValue();
      this.changeDetectorRef.detectChanges();
    };

    return combineLatest([this.digitalPropertyService.getDigitalPropertyTypes(), this.tenantInfo$])
      .pipe(
        map(([dpTypes, tenantInfo]: [IDigitalPropertyType[], ITenantInfoWithPackageInfo]): IDigitalPropertyType[] => {
          if (tenantInfo.packageName === PackageNames.ODT_Standalone) {
            return dpTypes.filter((dpType: IDigitalPropertyType): boolean => DigitalPropertyTypeUtility.isMobile(dpType));
          }
          return dpTypes;
        }),
      )
      .subscribe({ next: setDigitalPropertyTypes });
  }

  private setPropertyTypeValue(): void {
    if (typeof this.property !== 'undefined' && typeof this.property[$digitalProperty.typeId] !== 'undefined') {
      this.form.patchValue({
        [$digitalProperty.typeId]:
          typeof this.property.type === 'string' ? this.property.type : (this.property.type as IDigitalPropertyType)._id,
      });
    }
  }

  private setSelectedWorkspace(workspace: IWorkspace): void {
    this.selectedWorkspace = workspace;

    if (this.form.get($digitalProperty.workspaceId).value === null) {
      this.form.get($digitalProperty.workspaceId).setValue(this.selectedWorkspace[$workspace._id]);
    }
  }

  private onUserWorkspace(data: IWorkspace): void {
    this.setSelectedWorkspace(data);
    this.setExistingKeyValuesAndValidators();
    this.subscribeToConfidentialScanningFromTenant(data);
    this.changeDetectorRef.markForCheck();
    this.refreshDsInputCharacterCounts();
  }

  private getConfidentialScanningEnabledFromTenant(workspace: IWorkspace | null | undefined): Observable<boolean> {
    if (workspace?.[$workspace.tenant]?.[$tenant.privacySettings]) {
      return of(workspace[$workspace.tenant][$tenant.privacySettings][$privacySettings.privacyObfuscationEnabled] === true);
    }

    if (SharedCommonUtility.notNullishOrEmpty(this.workspaceId)) {
      return this.workspaceService.getWorkspace(this.workspaceId).pipe(
        map(
          (workspaceData: IWorkspace): boolean =>
            workspaceData?.[$workspace.tenant]?.[$tenant.privacySettings]?.[$privacySettings.privacyObfuscationEnabled] === true,
        ),
        take(1),
      );
    }

    if (SharedCommonUtility.notNullishOrEmpty(this.tenantId)) {
      return this.tenantInfo$.pipe(
        map(
          (tenant: ITenantInfoWithPackageInfo): boolean =>
            tenant?.[$tenant.privacySettings]?.[$privacySettings.privacyObfuscationEnabled] === true,
        ),
        take(1),
      );
    }

    return of(false);
  }

  private subscribeToConfidentialScanningFromTenant(workspace: IWorkspace | null | undefined): void {
    this.subscription.add(
      this.getConfidentialScanningEnabledFromTenant(workspace).subscribe((enabled: boolean): void => {
        this.applyConfidentialScanningSettings(enabled);
      }),
    );
  }

  private applyConfidentialScanningSettings(enabled: boolean): void {
    this.confidentialScanningEnabledTenantLevel = enabled;

    if (enabled) {
      this.confidentialScanningEnabled = true;
      this.emitFormChanges();
    }
  }

  private resetExistingKeyValuesAndValidators(): void {
    this.existingKeys = [];
    this.existingNames = [];
    this.form.get($digitalProperty.name).setValidators(this.getNameValidators());
    this.form.get($digitalProperty.name).updateValueAndValidity();
    this.form.get($digitalProperty.propertyKey).setValidators(this.getPropertyKeyValidators());
    this.form.get($digitalProperty.propertyKey).updateValueAndValidity();
  }

  private setExistingKeyValuesAndValidators(): void {
    const propertyId: string = this.digitalProperty?.[$digitalProperty._id];

    this.subscription.add(
      this.digitalPropertyService
        .existingKeyValues(this.workspaceId, $digitalProperty.name, propertyId)
        .subscribe((values: string[]) => {
          this.existingNames = values;
          this.form.get($digitalProperty.name).setValidators(this.getNameValidators());
          this.form.get($digitalProperty.name).updateValueAndValidity();
        }),
    );

    this.subscription.add(
      this.digitalPropertyService
        .existingKeyValues(this.workspaceId, $digitalProperty.propertyKey, propertyId)
        .subscribe((values: string[]) => {
          this.existingKeys = values;
          this.form.get($digitalProperty.propertyKey).setValidators(this.getPropertyKeyValidators());
          this.form.get($digitalProperty.propertyKey).updateValueAndValidity();
        }),
    );
  }

  private getFieldsToPreserve(): Partial<IDigitalProperty> {
    if (SharedCommonUtility.isNullish(this.digitalProperty)) {
      return {};
    }

    return {
      [$digitalProperty.noDomainRestrictions]: this.digitalProperty[$digitalProperty.noDomainRestrictions],
    };
  }

  @Input()
  public set digitalProperty(property: IDigitalProperty | IDigitalPropertyDetails) {
    this.property = property;

    if (SharedCommonUtility.getTypeOf(property) !== 'object') {
      return;
    }

    this.setExistingKeyValuesAndValidators();
    this.form.patchValue(property);
    this.setPropertyTypeValue();
    this.scanningAgentEnabled = property.useScanningAgent;
    this.confidentialScanningEnabled = property.privacyObfuscationEnabled || this.confidentialScanningEnabledTenantLevel;

    this.hasAccessibilityStatement = Boolean(property[$digitalProperty.accessibilityStatement]);
    this.onAccessibilityStatementSelectorChange(this.hasAccessibilityStatement);

    this.form.markAsUntouched();
    Object.values(this.form.controls).forEach((control: AbstractControl) => control.markAsUntouched());

    this.changeDetectorRef.detectChanges();
    this.refreshDsInputCharacterCounts();
  }

  private refreshDsInputCharacterCounts(): void {
    if (!this.digitalAssetFormUpdateFeatureEnabled) {
      return;
    }

    afterNextRender(
      (): void => {
        const inputs: HTMLInputElement[] = Array.from(
          this.elementRef.nativeElement.querySelectorAll<HTMLInputElement>('input[ds-input-field]'),
        );

        if (inputs.length === 0) {
          return;
        }

        inputs.forEach((input: HTMLInputElement): void => {
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });

        this.form.markAsPristine();
        this.form.markAsUntouched();
        Object.values(this.form.controls).forEach((control: AbstractControl): void => {
          control.markAsPristine();
          control.markAsUntouched();
        });

        this.changeDetectorRef.detectChanges();
      },
      { injector: this.injector },
    );
  }

  public get digitalProperty(): IDigitalProperty | IDigitalPropertyDetails {
    return this.property;
  }

  public get shouldDisplayWorkspaceName(): boolean {
    return this.selectedWorkspace && !this.disableWorkspaceValidation && !this.modalMode;
  }

  public shouldOverrideAuthorizedDomainsField(): boolean {
    return (
      this.isDomainRestrictionRegistered() &&
      this.isDomainRestrictionSupportedByDpType() &&
      this.form.get($digitalProperty.noDomainRestrictions).value !== true
    );
  }

  public toggleAuthorizedDomainState(): void {
    const noDomainRestrictions: boolean = this.form.get($digitalProperty.noDomainRestrictions).value;
    const authorizedDomainsControl: AbstractControl = this.form.get($digitalProperty.authorizedDomains);

    if (noDomainRestrictions) {
      authorizedDomainsControl.disable();
      authorizedDomainsControl.setValue('');
    } else {
      authorizedDomainsControl.enable();
      this.updateAuthorizedDomains();
    }
  }

  public updateAuthorizedDomains(): void {
    if (!this.shouldOverrideAuthorizedDomainsField()) {
      return;
    }

    const url: string = this.form.get($digitalProperty.url).value;
    if (SharedCommonUtility.notNullishOrEmpty(url?.trim())) {
      const authorizedDomains: string = SharedCommonUtility.getDomainPattern(url).join('\n');
      this.form.patchValue({
        [$digitalProperty.authorizedDomains]: authorizedDomains,
      });
    }
  }

  protected updateScanningAgentEnabled(value: boolean): void {
    this.scanningAgentEnabled = value;
    this.emitFormChanges();
  }

  public updateConfidentialScanningEnabled(value: boolean): void {
    if (this.confidentialScanningEnabledTenantLevel) {
      return;
    }

    this.confidentialScanningEnabled = Boolean(value);

    this.form.patchValue({
      [$digitalProperty.privacyObfuscationEnabled]: this.confidentialScanningEnabled,
    });

    this.emitFormChanges();
  }

  protected emitFormChanges(): void {
    const digitalPropertyFieldsToPreserve: Partial<IDigitalProperty> = this.getFieldsToPreserve();
    const digitalProperty: IDigitalProperty = {
      ...this.form.getRawValue(),
      [$digitalProperty.useScanningAgent]: this.scanningAgentEnabled,
      [$digitalProperty.workspaceId]: this.workspaceId,
      [$digitalProperty.noDomainRestrictions]: this.isWebsite === false,
      [$digitalProperty.privacyObfuscationEnabled]: this.confidentialScanningEnabled,
      ...digitalPropertyFieldsToPreserve,
    };

    this.formChanged.emit({
      data: CommonUtility.createFormData(digitalProperty),
      form: this.form,
      objectData: digitalProperty,
    });

    this.changeDetectorRef.detectChanges();
  }

  public onAccessibilityStatementSelectorChange(enabled: boolean): void {
    if (enabled) {
      this.form.registerControl(
        $digitalProperty.accessibilityStatement,
        this.formBuilder.control((this.digitalProperty && this.digitalProperty[$digitalProperty.accessibilityStatement]) || '', {
          validators: this.getUrlValidators(),
        }),
      );
    } else {
      this.form.removeControl($digitalProperty.accessibilityStatement);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['workspaceId']) {
      if (SharedCommonUtility.notNullishOrEmpty(changes['workspaceId'].currentValue)) {
        this.workspaceId = changes['workspaceId'].currentValue;
        this.subscription.add(
          this.workspaceService.getWorkspace(this.workspaceId).subscribe({ next: this.onUserWorkspace.bind(this) }),
        );
      }
    }
  }

  public ngOnInit(): void {
    this.subscription.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.digitalPropertyTagging, false)
        .pipe(map(Boolean), AngularUtility.shareRef())
        .subscribe((enabled: boolean) => {
          this.tagsFeatureEnabled = enabled;
          if (enabled) {
            if (!this.form.contains($digitalProperty.tags)) {
              this.form.addControl($digitalProperty.tags, new UntypedFormControl([]));
              this.changeDetectorRef.detectChanges();
            }
          } else {
            this.form.removeControl($digitalProperty.tags);
            this.changeDetectorRef.detectChanges();
          }
        }),
    );

    this.subscription.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.digitalAssetsPrivacySetting, false)
        .subscribe((enabled: boolean) => {
          this.digitalAssetsPrivacySettingFeatureEnabled = enabled;
          this.changeDetectorRef.detectChanges();
        }),
    );

    this.subscription.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.digitalAssetFormUpdate, false)
        .pipe(map(Boolean))
        .subscribe((enabled: boolean) => {
          this.digitalAssetFormUpdateFeatureEnabled = enabled;
          this.changeDetectorRef.detectChanges();
          if (enabled) {
            this.refreshDsInputCharacterCounts();
          }
        }),
    );

    if (this.disableWorkspaceValidation) {
      this.resetExistingKeyValuesAndValidators();
    }

    const workspace$: Observable<IWorkspace> = SharedCommonUtility.notNullishOrEmpty(this.workspaceId)
      ? this.workspaceService.getWorkspace(this.workspaceId).pipe(shareReplay(1))
      : NEVER;

    this.tenantInfo$ = SharedCommonUtility.notNullishOrEmpty(this.tenantId)
      ? this.tenantService.getTenantFromTenantedScope(this.tenantId).pipe(shareReplay(1))
      : workspace$.pipe(
          switchMap((workspace: IWorkspace) => this.tenantService.getTenantFromTenantedScope(workspace[$workspace.tenantId])),
          shareReplay(1),
        );

    this.subscription.add(workspace$.subscribe({ next: this.onUserWorkspace.bind(this) }));

    this.subscription.add(this.getDigitalPropertyTypes());

    this.subscription.add(
      this.form.valueChanges.subscribe({
        next: () => {
          this.emitFormChanges();
        },
      }),
    );

    this.subscribeToConfidentialScanningFromTenant(null);

    if (this.formValidationRequest$) {
      this.subscription.add(
        this.formValidationRequest$.subscribe(() => {
          const nameControl: AbstractControl = this.form.get($digitalProperty.name);
          const propertyKeyControl: AbstractControl = this.form.get($digitalProperty.propertyKey);
          nameControl.markAsDirty();
          nameControl.markAsTouched();
          propertyKeyControl.markAsDirty();
          propertyKeyControl.markAsTouched();
          this.changeDetectorRef.detectChanges();
        }),
      );
    }

    this.emitFormChanges();
  }

  public ngAfterViewInit(): void {
    this.refreshDsInputCharacterCounts();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.urlChangeSubscription?.unsubscribe();
    this.noDomainRestrictionSubscription?.unsubscribe();
  }

  protected readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;
  protected readonly DsInputStates: typeof DsInputStates = DsInputStates;

  protected get propertyKeyInputState(): DsInputStates {
    return this.property ? DsInputStates.inactive : DsInputStates.active;
  }
  protected readonly BannerType: typeof BannerType = BannerType;
  protected readonly Icons: typeof Icons = Icons;
}
